import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './ActivityGrid.css';

interface DayData {
  date: Date;
  count: number;
}

const ActivityGrid: React.FC = () => {
  const { user } = useAuth();
  const [gridData, setGridData] = useState<DayData[][]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hoveredCell, setHoveredCell] = useState<{ week: number; day: number } | null>(null);

  useEffect(() => {
    const fetchActivityData = async () => {
      if (!user?.issuer) return;

      try {
        const response = await fetch(`${API_URL}/user/parlays/active?user_issuer=${user.issuer}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error('Failed to fetch parlays');

        const data = await response.json();
        const parlays = data.parlays || [];

        // Count parlays per day
        const countsByDate: { [key: string]: number } = {};
        parlays.forEach((parlay: any) => {
          if (parlay.created_at) {
            const date = new Date(parlay.created_at);
            const dateKey = date.toISOString().split('T')[0];
            countsByDate[dateKey] = (countsByDate[dateKey] || 0) + 1;
          }
        });

        // Generate 365 days of data
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - 364);

        // Adjust to start on Sunday
        const dayOfWeek = startDate.getDay();
        startDate.setDate(startDate.getDate() - dayOfWeek);

        const weeks: DayData[][] = [];
        let currentWeek: DayData[] = [];
        let currentDate = new Date(startDate);
        let total = 0;

        // Generate grid data (weeks × 7 days)
        for (let i = 0; i < 371; i++) {
          const dateKey = currentDate.toISOString().split('T')[0];
          const count = countsByDate[dateKey] || 0;
          total += count;

          currentWeek.push({
            date: new Date(currentDate),
            count: count
          });

          if (currentWeek.length === 7) {
            weeks.push(currentWeek);
            currentWeek = [];
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }

        if (currentWeek.length > 0) {
          weeks.push(currentWeek);
        }

        setGridData(weeks);
        setTotalCount(total);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching activity data:', err);
        setLoading(false);
      }
    };

    fetchActivityData();
  }, [user?.issuer]);

  const getColorLevel = (count: number): number => {
    if (count === 0) return 0;      // level-0: no activity
    if (count === 1) return 1;      // level-1: one parlay
    if (count === 2) return 2;      // level-2: two parlays (darker than 1)
    if (count <= 4) return 3;       // level-3: moderate activity
    return 4;                        // level-4: high activity (5+)
  };

  const getMonthLabel = (weekIndex: number): string | null => {
    if (weekIndex >= gridData.length) return null;
    const firstDayOfWeek = gridData[weekIndex][0].date;
    const month = firstDayOfWeek.toLocaleDateString('en-US', { month: 'short' });

    // Only show if it's the first week of the month
    if (firstDayOfWeek.getDate() <= 7) {
      return month;
    }
    return null;
  };

  if (loading) {
    return <div className="activity-grid-loading">Loading activity...</div>;
  }

  return (
    <div className="activity-grid-container">
      <div className="activity-header">
        <span className="activity-count">{totalCount} parlays in the last year</span>
      </div>

      <div className="activity-grid-wrapper">
        <div className="activity-grid">
          {/* Month labels */}
          <div className="month-labels">
            <div className="weekday-spacer"></div>
            {gridData.map((_, weekIndex) => {
              const monthLabel = getMonthLabel(weekIndex);
              return (
                <div key={weekIndex} className="month-label">
                  {monthLabel}
                </div>
              );
            })}
          </div>

          {/* Grid with weekday labels */}
          <div className="grid-with-labels">
            <div className="weekday-labels">
              <div className="weekday-label">Mon</div>
              <div className="weekday-label"></div>
              <div className="weekday-label">Wed</div>
              <div className="weekday-label"></div>
              <div className="weekday-label">Fri</div>
              <div className="weekday-label"></div>
              <div className="weekday-label">Sun</div>
            </div>

            <div className="grid-columns">
              {gridData.map((week, weekIndex) => (
                <div key={weekIndex} className="grid-column">
                  {week.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={`grid-cell level-${getColorLevel(day.count)}`}
                      onMouseEnter={() => setHoveredCell({ week: weekIndex, day: dayIndex })}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {hoveredCell?.week === weekIndex && hoveredCell?.day === dayIndex && (
                        <div className="cell-tooltip">
                          {day.count} parlay{day.count !== 1 ? 's' : ''} on {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="activity-legend">
          <span className="legend-label">Less</span>
          <div className="legend-cell level-0"></div>
          <div className="legend-cell level-1"></div>
          <div className="legend-cell level-2"></div>
          <div className="legend-cell level-3"></div>
          <div className="legend-cell level-4"></div>
          <span className="legend-label">More</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityGrid;
